import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';

const TRADITIONS = ['Kagyu', 'Nyingma', 'Gelug', 'Sakya', 'Zen', 'Theravada', 'Vajrayana', 'Other'];
const LANGUAGES = ['English', 'Tibetan', 'Sanskrit', 'Chinese', 'Japanese', 'Pali', 'Other'];
const FILE_TYPES = ['pdf', 'html', 'epub', 'image', 'other'];

const defaultForm = {
    title: '',
    author: '',
    tradition: '',
    language: '',
    fileType: '',
    description: '',
    tags: '',
    featured: false,
};

const AdminLibrary = () => {
    const { user, isAuthenticated } = useAuth();
    const [form, setForm] = useState(defaultForm);
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [successWorkId, setSuccessWorkId] = useState(null);
    const [error, setError] = useState('');

    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const tagList = form.tags
        ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessWorkId(null);

        if (!form.title.trim()) {
            setError('Title is required.');
            return;
        }
        if (!form.fileType) {
            setError('File type is required.');
            return;
        }
        if (!file) {
            setError('Please select a file to upload.');
            return;
        }

        setIsUploading(true);
        try {
            // 1. Get auth token
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();

            // 2. Get presigned upload URL
            const metaRes = await fetch(`${API_URL}/admin/library-upload-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: form.title.trim(),
                    author: form.author.trim(),
                    tradition: form.tradition,
                    language: form.language,
                    fileType: form.fileType,
                    description: form.description.trim(),
                    tags: tagList,
                    featured: form.featured,
                    filename: file.name,
                    contentType: file.type,
                }),
            });

            if (!metaRes.ok) {
                const errData = await metaRes.json().catch(() => ({}));
                throw new Error(errData.message || `Failed to get upload URL (${metaRes.status})`);
            }

            const { uploadUrl, workId } = await metaRes.json();

            // 3. Upload file to S3 presigned URL (no auth header)
            const uploadRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file,
            });

            if (!uploadRes.ok) {
                throw new Error('File upload to storage failed. Please try again.');
            }

            // 4. Success
            setSuccessWorkId(workId);
            setForm(defaultForm);
            setFile(null);
        } catch (err) {
            setError(err.message || 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 py-12 px-6">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-1">Dharma Library Upload</h1>
                    <p className="text-gray-400 text-sm">Add a new work to the shared dharma collection.</p>
                </div>

                <div className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-xl p-7">
                    {/* Success message */}
                    {successWorkId && (
                        <div className="mb-6 p-4 bg-green-900/40 border border-green-600 rounded-lg">
                            <p className="text-green-300 font-medium">Work uploaded successfully!</p>
                            <Link
                                to={`/library/works/${successWorkId}`}
                                className="text-amber-400 hover:underline text-sm mt-1 inline-block"
                            >
                                View Work
                            </Link>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-900/40 border border-red-600 rounded-lg">
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Title */}
                        <div>
                            <label htmlFor="adl-title" className="block text-sm font-medium text-gray-300 mb-1">
                                Title <span className="text-red-400">*</span>
                            </label>
                            <input
                                id="adl-title"
                                type="text"
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                required
                                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                placeholder="Enter work title"
                            />
                        </div>

                        {/* Author */}
                        <div>
                            <label htmlFor="adl-author" className="block text-sm font-medium text-gray-300 mb-1">
                                Author
                            </label>
                            <input
                                id="adl-author"
                                type="text"
                                name="author"
                                value={form.author}
                                onChange={handleChange}
                                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                placeholder="Author or translator"
                            />
                        </div>

                        {/* Tradition + Language */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="adl-tradition" className="block text-sm font-medium text-gray-300 mb-1">
                                    Tradition
                                </label>
                                <select
                                    id="adl-tradition"
                                    name="tradition"
                                    value={form.tradition}
                                    onChange={handleChange}
                                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                >
                                    <option value="">Select tradition</option>
                                    {TRADITIONS.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="adl-language" className="block text-sm font-medium text-gray-300 mb-1">
                                    Language
                                </label>
                                <select
                                    id="adl-language"
                                    name="language"
                                    value={form.language}
                                    onChange={handleChange}
                                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                >
                                    <option value="">Select language</option>
                                    {LANGUAGES.map((l) => (
                                        <option key={l} value={l}>{l}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* File Type */}
                        <div>
                            <label htmlFor="adl-filetype" className="block text-sm font-medium text-gray-300 mb-1">
                                File Type <span className="text-red-400">*</span>
                            </label>
                            <select
                                id="adl-filetype"
                                name="fileType"
                                value={form.fileType}
                                onChange={handleChange}
                                required
                                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                            >
                                <option value="">Select file type</option>
                                {FILE_TYPES.map((ft) => (
                                    <option key={ft} value={ft}>{ft.toUpperCase()}</option>
                                ))}
                            </select>
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="adl-description" className="block text-sm font-medium text-gray-300 mb-1">
                                Description
                            </label>
                            <textarea
                                id="adl-description"
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                rows={3}
                                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 resize-vertical"
                                placeholder="Brief description of this work"
                            />
                        </div>

                        {/* Tags */}
                        <div>
                            <label htmlFor="adl-tags" className="block text-sm font-medium text-gray-300 mb-1">
                                Tags <span className="text-gray-500 font-normal">(comma-separated)</span>
                            </label>
                            <input
                                id="adl-tags"
                                type="text"
                                name="tags"
                                value={form.tags}
                                onChange={handleChange}
                                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                placeholder="e.g. meditation, Mahamudra, practice"
                            />
                            {tagList.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {tagList.map((tag) => (
                                        <span key={tag} className="text-xs bg-slate-600 text-gray-300 px-2 py-0.5 rounded-full">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Featured */}
                        <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="featured"
                                    checked={form.featured}
                                    onChange={handleChange}
                                    className="w-4 h-4 accent-amber-500"
                                />
                                <span className="text-sm text-gray-300">Featured work</span>
                            </label>
                        </div>

                        {/* File picker */}
                        <div>
                            <label htmlFor="adl-file" className="block text-sm font-medium text-gray-300 mb-1">
                                File <span className="text-red-400">*</span>
                            </label>
                            <input
                                id="adl-file"
                                type="file"
                                onChange={(e) => setFile(e.target.files[0] || null)}
                                className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-slate-600 file:text-white hover:file:bg-slate-500 file:cursor-pointer"
                            />
                            {file && (
                                <p className="text-gray-500 text-xs mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isUploading}
                            className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors"
                        >
                            {isUploading ? 'Uploading...' : 'Upload Work'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminLibrary;
